import { useCallback, useEffect, useMemo, useState } from "react";
import { Collapse } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import Card from "../../../../components/card/card";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import ApiTaskManagement from "../../../../api/task-managements";

// Lists every task an admin has manually attached to the subject — past,
// today, and future. Tasks dated today-or-later are shown directly so the
// admin sees what's incoming without extra clicks. Older entries are tucked
// into a collapse (closed by default) to keep the page short for long
// assignment histories. Rows are clickable: opening one routes through
// <AssignedTaskDrawer> (via the parent's `onOpenTask`).
const AssignmentRow = ({ task, tz, onOpenTask }) => {
  const taskTz = task.plannedTimeZone || tz;
  const planned = task.plannedDate
    ? moment.tz(task.plannedDate, "YYYY-MM-DD", taskTz)
    : moment.tz(task.plannedAt, taskTz);
  const plannedTime = moment.tz(task.plannedAt, taskTz);
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`assigned-task-${task.id}`}
      onClick={() => onOpenTask && onOpenTask(task)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onOpenTask && onOpenTask(task);
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        border: "1px solid var(--border-color, rgba(108,108,137,0.16))",
        borderRadius: 8,
        cursor: "pointer",
      }}
    >
      {task.DailyTask?.backgroundColor && (
        <span
          style={{
            width: 8,
            height: 28,
            borderRadius: 2,
            background:
              task.DailyTask.middleColor ||
              task.DailyTask.backgroundColor,
            flexShrink: 0,
          }}
        />
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {task.DailyTask?.title || "Task"}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
          {planned.format("ddd, DD MMM YYYY")}
          {task.remind && Number.isInteger(task.remindHour)
            ? ` · ${plannedTime.format("HH:mm")}`
            : ""}
        </div>
      </div>
      <UserActivityStatus
        userStatus={task.status || "PENDING"}
        style={{ flexShrink: 0 }}
      />
    </div>
  );
};

const UpcomingAssignments = ({
  subjectId,
  timezone,
  refreshTick,
  onOpenTask,
}) => {
  const tz = timezone || "UTC";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAssignments = useCallback(async () => {
    if (!subjectId) return;
    setLoading(true);
    try {
      const { data } = await ApiTaskManagement.getUpcomingAssignments(subjectId);
      setItems(data?.tasks || []);
    } catch (_e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [subjectId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments, refreshTick]);

  // Split by planned local calendar date first. Terminal statuses belong in
  // history only when their planned day is today or earlier; a bad or stale
  // INCOMPLETED status on a future assignment must not make it look "past".
  const TERMINAL_STATUSES = ["COMPLETED", "DISMISSED", "INCOMPLETED"];
  const { current, past } = useMemo(() => {
    const todayKey = moment.tz(tz).format("YYYY-MM-DD");
    const cur = [];
    const old = [];
    for (const task of items) {
      const dateKey =
        task.plannedDate ||
        (task.plannedAt
          ? moment.tz(task.plannedAt, tz).format("YYYY-MM-DD")
          : null);
      const isTerminal = TERMINAL_STATUSES.includes(task.status);
      const isPastDate = dateKey && dateKey < todayKey;
      const isToday = dateKey === todayKey;

      if (isPastDate || (isTerminal && isToday)) {
        old.push(task);
      } else {
        cur.push(task);
      }
    }
    const dateKey = (task) =>
      task.plannedDate ||
      (task.plannedAt
        ? moment.tz(task.plannedAt, task.plannedTimeZone || tz).format("YYYY-MM-DD")
        : "");
    const timestampKey = (task) =>
      task.plannedAt ? moment(task.plannedAt).valueOf() : 0;

    // Both sections are newest-first: newer planned dates above older ones.
    const newestFirst = (a, b) => {
      const dateDiff = dateKey(b).localeCompare(dateKey(a));
      return dateDiff || timestampKey(b) - timestampKey(a);
    };
    cur.sort(newestFirst);
    old.sort(newestFirst);
    return { current: cur, past: old };
  }, [items, tz]);

  if (!loading && items.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <b>Assigned Tasks</b>
            <span style={{ marginLeft: 8, color: "var(--text-2)", fontSize: 13 }}>
              {items.length} task{items.length === 1 ? "" : "s"}
            </span>
          </div>

          {current.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginBottom: past.length > 0 ? 12 : 0,
              }}
            >
              {current.map((task) => (
                <AssignmentRow
                  key={task.id}
                  task={task}
                  tz={tz}
                  onOpenTask={onOpenTask}
                />
              ))}
            </div>
          )}

          {past.length > 0 && (
            <Collapse
              ghost
              className="assigned-tasks-collapse"
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
              items={[
                {
                  key: "past",
                  label: (
                    <span style={{ color: "var(--text-2)" }}>
                      Past assignments
                      <span style={{ marginLeft: 6 }}>({past.length})</span>
                    </span>
                  ),
                  children: (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {past.map((task) => (
                        <AssignmentRow
                          key={task.id}
                          task={task}
                          tz={tz}
                          onOpenTask={onOpenTask}
                        />
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>
      </Card>
      {/* antd's Collapse content box has its own horizontal padding by
          default, which would push the past-rows narrower than the current-
          rows next to them. Match widths by zero-ing the inner padding —
          scoped to this drawer's collapse only via .assigned-tasks-collapse. */}
      <style>{`
        .assigned-tasks-collapse .ant-collapse-content > .ant-collapse-content-box {
          padding: 0;
        }
        .assigned-tasks-collapse .ant-collapse-header {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default UpcomingAssignments;
